<template>
  <div class="invoice-editor-container">
    <!-- Header -->
    <div class="editor-header">
      <div class="header-left">
        <button class="back-btn" @click="$router.push('/invoicing')">
          <i class="fas fa-arrow-left"></i>
        </button>
        <div>
          <h1 class="page-title">
            {{ isEditing ? 'Edit Invoice' : 'New Invoice' }}
            <span v-if="form.invoice_number" class="invoice-num-badge">{{ form.invoice_number }}</span>
          </h1>
          <p class="page-subtitle">{{ isEditing ? `Editing ${form.client_organization_name}` : 'Create a new invoice for a client' }}</p>
        </div>
      </div>
      <div class="header-actions">
        <button class="btn btn-secondary" @click="saveInvoice('DRAFT')" :disabled="saving">
          <i class="fas fa-save"></i>
          {{ saving === 'DRAFT' ? 'Saving...' : 'Save Draft' }}
        </button>
        <button class="btn btn-primary" @click="saveInvoice('SENT')" :disabled="saving">
          <i class="fas fa-paper-plane"></i>
          {{ saving === 'SENT' ? 'Saving...' : 'Save & Mark Sent' }}
        </button>
      </div>
    </div>

    <div class="editor-body">
      <!-- Left Column: Invoice Meta -->
      <div class="meta-column">

        <!-- Client & Dates Card -->
        <div class="card">
          <h3 class="card-title"><i class="fas fa-user-tie"></i> Invoice Details</h3>

          <div class="form-group">
            <label>Client Organization <span class="required">*</span></label>
            <select v-model="form.client_organization" class="form-control" @change="onClientChange">
              <option value="">Select a client...</option>
              <option v-for="client in clients" :key="client.id" :value="client.id">
                {{ client.name }}
              </option>
            </select>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label>Issue Date</label>
              <input v-model="form.issue_date" type="date" class="form-control" />
            </div>
            <div class="form-group">
              <label>Due Date</label>
              <input v-model="form.due_date" type="date" class="form-control" />
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label>Billing Period Start</label>
              <input
                v-model="form.billing_period_start"
                type="date"
                class="form-control"
                @change="onBillingPeriodChange"
              />
            </div>
            <div class="form-group">
              <label>Billing Period End</label>
              <input
                v-model="form.billing_period_end"
                type="date"
                class="form-control"
                @change="onBillingPeriodChange"
              />
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label>Tax Rate (%)</label>
              <input v-model="form.tax_rate_pct" type="number" min="0" max="100" step="0.01" class="form-control" placeholder="0.00" />
            </div>
            <div class="form-group">
              <label>Currency</label>
              <select v-model="form.currency" class="form-control">
                <option value="USD">USD</option>
                <option value="CAD">CAD</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
              </select>
            </div>
          </div>

          <div class="form-group">
            <label>Notes</label>
            <textarea v-model="form.notes" class="form-control" rows="3" placeholder="Payment terms, special instructions..."></textarea>
          </div>

          <!-- Invoicing From (appears on PDF) -->
          <div class="from-section">
            <div class="from-section-title"><i class="fas fa-building"></i> Billed From (appears on invoice)</div>
            <div class="form-group">
              <label>Business Name</label>
              <input v-model="form.from_name" type="text" class="form-control" placeholder="Your LLC or business name" />
            </div>
            <div class="form-row">
              <div class="form-group">
                <label>Email</label>
                <input v-model="form.from_email" type="email" class="form-control" placeholder="billing@yourco.com" />
              </div>
            </div>
            <div class="form-group">
              <label>Address</label>
              <input v-model="form.from_address" type="text" class="form-control" placeholder="123 Main St, City, State ZIP" />
            </div>
          </div>
        </div>

        <!-- Import Panel -->
        <div class="card import-card" v-if="form.client_organization && form.billing_period_start && form.billing_period_end">
          <h3 class="card-title"><i class="fas fa-cloud-download-alt"></i> Import Line Items</h3>

          <!-- GitHub Panel -->
          <div class="import-section">
            <div class="import-section-header">
              <div class="import-label">
                <i class="fab fa-github"></i>
                GitHub Commits
              </div>
              <button class="btn-import" @click="fetchGitHubCommits" :disabled="loadingGitHub">
                <i :class="loadingGitHub ? 'fas fa-spinner fa-spin' : 'fas fa-sync'"></i>
                {{ loadingGitHub ? 'Fetching...' : 'Fetch Commits' }}
              </button>
            </div>
            <div v-if="githubError" class="import-error">{{ githubError }}</div>
            <div v-if="availableCommits.length > 0" class="commit-list">
              <div class="commit-list-header">
                <label class="check-all">
                  <input type="checkbox" @change="toggleAllCommits" :checked="allCommitsSelected" />
                  Select All ({{ filteredCommits.length }} commits)
                </label>
                <div class="commit-actions">
                  <button
                    class="btn-sm btn-sm-ai"
                    @click="summarizeCommits"
                    :disabled="loadingSummarize || selectedCommits.length === 0"
                    title="Use AI to group and summarize selected commits into invoice line items"
                  >
                    <i :class="loadingSummarize ? 'fas fa-spinner fa-spin' : 'fas fa-wand-magic-sparkles'"></i>
                    {{ loadingSummarize ? 'Summarizing...' : `Summarize ${selectedCommits.length} with AI` }}
                  </button>
                  <button class="btn-sm" @click="addSelectedCommits" :disabled="selectedCommits.length === 0">
                    Add Raw ({{ selectedCommits.length }})
                  </button>
                </div>
              </div>

              <div v-if="availableContributors.length" class="contributor-filter">
                <div class="contributor-filter-label">
                  Invoice work by contributor
                  <span v-if="excludedContributorCommitCount">
                    · {{ excludedContributorCommitCount }} commit(s) excluded
                  </span>
                </div>
                <label
                  v-for="contributor in availableContributors"
                  :key="contributor.key"
                  class="contributor-option"
                >
                  <input
                    type="checkbox"
                    :checked="selectedContributorKeys.includes(contributor.key)"
                    @change="toggleContributor(contributor.key, $event.target.checked)"
                  />
                  {{ contributor.label }} ({{ contributor.count }})
                  <span v-if="contributor.excludedByDefault" class="contributor-default-note">
                    excluded by default
                  </span>
                </label>
              </div>

              <!-- AI Summary Preview -->
              <div v-if="summarizeError" class="import-error">{{ summarizeError }}</div>
              <div v-if="aiSummaryItems.length > 0" class="ai-summary-panel">
                <div class="ai-summary-header">
                  <span><i class="fas fa-robot"></i> AI grouped {{ aiSummaryItems.length }} line items from {{ selectedCommits.length || 'selected' }} commits</span>
                  <button class="ai-dismiss" @click="aiSummaryItems = []" title="Dismiss">
                    <i class="fas fa-times"></i>
                  </button>
                </div>
                <div
                  v-for="(item, idx) in aiSummaryItems"
                  :key="idx"
                  class="ai-summary-item"
                >
                  <div class="ai-item-row">
                    <span class="ai-item-type">{{ item.line_type }}</span>
                    <button class="ai-item-remove" @click="aiSummaryItems.splice(idx, 1)" title="Remove this item">
                      <i class="fas fa-times"></i>
                    </button>
                  </div>
                  <textarea
                    v-model="item.description"
                    class="ai-item-edit"
                    rows="2"
                    placeholder="Description..."
                  ></textarea>
                </div>
                <button class="btn-add-to-invoice" @click="addAISummaryItems">
                  <i class="fas fa-file-invoice"></i>
                  Add {{ aiSummaryItems.length }} Items to Invoice
                </button>
              </div>

              <div v-for="commit in filteredCommits" :key="commit.sha" class="commit-item">
                <label class="commit-checkbox">
                  <input type="checkbox" :value="commit.sha" v-model="selectedCommits" />
                  <div class="commit-info">
                    <span class="commit-sha">{{ commit.short_sha }}</span>
                    <span class="commit-repo">{{ commit.repo }}</span>
                    <span class="commit-date">{{ formatDateShort(commit.date) }}</span>
                    <span class="commit-author">
                      {{ commit.author_name || commit.author_login || commit.author_email || 'Unknown author' }}
                    </span>
                    <span class="commit-msg">{{ commit.message }}</span>
                  </div>
                </label>
              </div>
            </div>
            <div v-else-if="!loadingGitHub && githubNoMappings" class="no-mappings-msg">
              <i class="fas fa-map-marker-alt"></i>
              No repos mapped to this client yet.
              <router-link to="/api-integrations" target="_blank">Set up mappings in API Integrations → GitHub tab</router-link>
            </div>
            <div v-else-if="!loadingGitHub && githubFetched" class="no-data">
              No commits found in this billing period.
            </div>
          </div>

          <!-- Token Usage Panel -->
          <div class="import-section">
            <div class="import-section-header">
              <div class="import-label">
                <i class="fas fa-microchip"></i>
                Token Usage
              </div>
              <button class="btn-import" @click="fetchTokenUsage" :disabled="loadingTokens">
                <i :class="loadingTokens ? 'fas fa-spinner fa-spin' : 'fas fa-sync'"></i>
                {{ loadingTokens ? 'Fetching...' : 'Fetch Usage' }}
              </button>
            </div>
            <div v-if="tokenError" class="import-error">{{ tokenError }}</div>
            <div v-if="tokenUsageSummary" class="usage-summary">
              <div class="usage-stats">
                <span>{{ tokenUsageSummary.total_tokens?.toLocaleString() }} tokens</span>
                <span class="usage-cost">${{ tokenUsageSummary.total_cost_usd }}</span>
                <span>{{ tokenUsageSummary.call_count }} calls</span>
              </div>
              <button
                class="btn-sm btn-sm-primary"
                @click="addTokenUsageItems"
                :disabled="!tokenUsageSummary.line_items?.length"
              >
                Add All Usage Items ({{ tokenUsageSummary.line_items?.length || 0 }})
              </button>
            </div>
            <div v-else-if="!loadingTokens && tokensFetched" class="no-data">
              No token usage found for this period.
            </div>
          </div>

          <!-- Subscription Panel -->
          <div class="import-section">
            <div class="import-section-header">
              <div class="import-label">
                <i class="fas fa-repeat"></i>
                Subscription Fee
              </div>
              <button class="btn-import" @click="fetchSubscription" :disabled="loadingSubscription">
                <i :class="loadingSubscription ? 'fas fa-spinner fa-spin' : 'fas fa-sync'"></i>
                {{ loadingSubscription ? 'Fetching...' : 'Fetch Subscription' }}
              </button>
            </div>
            <div v-if="subscriptionError" class="import-error">{{ subscriptionError }}</div>
            <div v-if="subscriptionItem" class="subscription-item">
              <div class="sub-description">{{ subscriptionItem.description }}</div>
              <div class="sub-footer">
                <strong>${{ subscriptionItem.amount }}</strong>
                <button class="btn-sm btn-sm-primary" @click="addSubscriptionItem">
                  <i class="fas fa-plus"></i> Add
                </button>
              </div>
            </div>
            <div v-else-if="!loadingSubscription && subscriptionFetched" class="no-data">
              No active subscription found.
            </div>
          </div>

          <!-- AWS Platform Infrastructure Panel -->
          <div class="import-section">
            <div class="import-section-header">
              <div class="import-label">
                <i class="fab fa-aws"></i>
                AWS Platform Share
              </div>
              <button class="btn-import" @click="fetchAwsPlatform" :disabled="loadingAws">
                <i :class="loadingAws ? 'fas fa-spinner fa-spin' : 'fas fa-sync'"></i>
                {{ loadingAws ? 'Fetching...' : 'Fetch AWS Costs' }}
              </button>
            </div>

            <div class="aws-split-row">
              <label class="aws-split-label">Shared platform split</label>
              <select v-model="sharedSplitRatio" class="form-control aws-split-select">
                <option value="0.22">22% (Fargate vCPU share)</option>
                <option value="0.50">50 / 50</option>
                <option value="0.78">78% (Praetorian-heavy inverse)</option>
              </select>
            </div>

            <div v-if="awsError" class="import-error">{{ awsError }}</div>
            <div v-if="awsPeriodStale" class="import-error">
              Billing period changed since the last AWS fetch
              (was {{ awsFetchedFor.start }} → {{ awsFetchedFor.end }}).
              Fetch again before adding items.
            </div>
            <div v-if="awsLastAddedNote" class="aws-added-note">
              {{ awsLastAddedNote }}
            </div>

            <div v-if="awsPlatformSummary" class="aws-summary">
              <div class="aws-fetched-period">
                Showing Cost Explorer for
                <strong>{{ awsPlatformSummary.period_label }}</strong>
                ({{ awsPlatformSummary.period_start }} → {{ awsPlatformSummary.period_end }})
              </div>
              <div
                class="aws-reconcile"
                :class="awsPlatformSummary.reconciliation_ok ? 'ok' : 'bad'"
              >
                <i
                  :class="awsPlatformSummary.reconciliation_ok
                    ? 'fas fa-check-circle'
                    : 'fas fa-exclamation-triangle'"
                ></i>
                <div>
                  <strong>
                    {{ awsPlatformSummary.client_organization_name }}:
                    ${{ awsPlatformSummary.client_share_usd }}
                  </strong>
                  +
                  <strong>
                    {{ awsPlatformSummary.issuer_display_name }}:
                    ${{ awsPlatformSummary.issuer_share_usd }}
                  </strong>
                  =
                  <strong>Account ${{ awsPlatformSummary.account_total_usd }}</strong>
                  <span v-if="!awsPlatformSummary.reconciliation_ok">
                    (delta ${{ awsPlatformSummary.reconciliation_delta_usd }})
                  </span>
                </div>
              </div>

              <div class="usage-stats aws-stats">
                <span>
                  Dedicated: <strong>${{ awsPlatformSummary.dedicated_usd }}</strong>
                </span>
                <span>
                  Shared ({{ awsPlatformSummary.shared_split_pct }}%):
                  <strong>${{ awsSelectedSharedTotal.toFixed(2) }}</strong>
                </span>
                <span>
                  Untagged selected:
                  <strong>${{ awsSelectedUntaggedTotal.toFixed(2) }}</strong>
                </span>
                <span class="usage-cost">
                  Bill this client: ${{ awsSelectedBillableTotal.toFixed(2) }}
                  <span class="aws-svc-raw">
                    ({{ awsSelectedKeys.length }} lines)
                  </span>
                </span>
              </div>

              <div
                v-if="awsDedicatedServices.length"
                class="aws-service-group"
              >
                <div class="aws-service-group-title">
                  <label class="aws-select-all">
                    <input
                      type="checkbox"
                      :checked="awsGroupAllSelected(awsDedicatedServices)"
                      @change="toggleAwsGroup(awsDedicatedServices, $event.target.checked)"
                    />
                    {{ awsPlatformSummary.client_organization_name }} dedicated (100%)
                  </label>
                </div>
                <div
                  v-for="(svc, idx) in awsDedicatedServices"
                  :key="'d-' + idx"
                  class="aws-service-row"
                >
                  <label class="aws-svc-check">
                    <input
                      type="checkbox"
                      :value="svc.selection_key"
                      v-model="awsSelectedKeys"
                    />
                    <span class="aws-svc-name">{{ svc.service_label }}</span>
                  </label>
                  <span class="aws-svc-amt">${{ svc.invoice_amount_usd || svc.client_amount_usd }}</span>
                </div>
              </div>

              <div
                v-if="awsIssuerServices.length"
                class="aws-service-group aws-issuer"
              >
                <div class="aws-service-group-title">
                  {{ awsPlatformSummary.issuer_display_name }} dedicated
                  (never billed on this {{ awsPlatformSummary.client_organization_name }} invoice)
                </div>
                <div
                  v-for="(svc, idx) in awsIssuerServices"
                  :key="'i-' + idx"
                  class="aws-service-row aws-row-excluded"
                >
                  <span class="aws-svc-name">
                    <span class="aws-tag">[{{ svc.project }}]</span>
                    {{ svc.service_label }}
                  </span>
                  <span class="aws-svc-amt">${{ svc.issuer_amount_usd }}</span>
                </div>
              </div>

              <div
                v-if="awsSharedServices.length"
                class="aws-service-group"
              >
                <div class="aws-service-group-title">
                  <label class="aws-select-all">
                    <input
                      type="checkbox"
                      :checked="awsGroupAllSelected(awsSharedServices)"
                      @change="toggleAwsGroup(awsSharedServices, $event.target.checked)"
                    />
                    Shared ({{ awsPlatformSummary.shared_split_pct }}% /
                    {{ awsPlatformSummary.issuer_split_pct }}%)
                  </label>
                </div>
                <div
                  v-for="(svc, idx) in awsSharedServices"
                  :key="'s-' + idx"
                  class="aws-service-row"
                  :class="{ 'aws-row-off': !awsSelectedKeys.includes(svc.selection_key) }"
                >
                  <label class="aws-svc-check">
                    <input
                      type="checkbox"
                      :value="svc.selection_key"
                      v-model="awsSelectedKeys"
                    />
                    <span class="aws-svc-name">
                      {{ svc.service_label }}
                      <span class="aws-svc-raw">${{ svc.raw_amount_usd }} pool</span>
                    </span>
                  </label>
                  <span class="aws-svc-amt">
                    ${{ svc.invoice_amount_usd || svc.client_amount_usd }}
                  </span>
                </div>
              </div>

              <div
                v-if="awsUntaggedServices.length"
                class="aws-service-group aws-untagged"
              >
                <div class="aws-service-group-title">
                  <label class="aws-select-all">
                    <input
                      type="checkbox"
                      :checked="awsGroupAllSelected(awsUntaggedServices)"
                      @change="toggleAwsGroup(awsUntaggedServices, $event.target.checked)"
                    />
                    Untagged — select to bill 100% to
                    {{ awsPlatformSummary.client_organization_name }}
                  </label>
                </div>
                <p class="aws-forecast-hint">
                  Stack services (ECS, RDS, ElastiCache, NAT/VPC, …) default on.
                  Issuer leftovers (WorkMail, SES, …) default off. Unchecked
                  stays with {{ awsPlatformSummary.issuer_display_name }}.
                </p>
                <div
                  v-for="(svc, idx) in awsUntaggedServices"
                  :key="'u-' + idx"
                  class="aws-service-row"
                  :class="{ 'aws-row-off': !awsSelectedKeys.includes(svc.selection_key) }"
                >
                  <label class="aws-svc-check">
                    <input
                      type="checkbox"
                      :value="svc.selection_key"
                      v-model="awsSelectedKeys"
                    />
                    <span class="aws-svc-name">
                      <span class="aws-tag">[UNTAGGED]</span>
                      {{ svc.service_label }}
                      <span class="aws-svc-raw">${{ svc.raw_amount_usd }}</span>
                    </span>
                  </label>
                  <span class="aws-svc-amt">
                    ${{ svc.invoice_amount_usd || svc.raw_amount_usd }}
                  </span>
                </div>
              </div>

              <p class="aws-methodology">{{ awsPlatformSummary.methodology }}</p>

              <div
                v-if="awsForecast6 || awsForecast12"
                class="aws-service-group aws-forecast"
              >
                <div class="aws-service-group-title">
                  Budget estimate (CE forecast + unbudgeted buffer — not charged)
                </div>
                <div class="aws-split-row aws-forecast-buffer">
                  <label class="aws-split-label">Unbudgeted buffer</label>
                  <select
                    v-model="unbudgetedRatio"
                    class="form-control aws-split-select"
                    @change="fetchAwsPlatform"
                  >
                    <option value="0">None (0%)</option>
                    <option value="0.10">10%</option>
                    <option value="0.15">15%</option>
                    <option value="0.20">20% (default)</option>
                    <option value="0.25">25%</option>
                  </select>
                </div>
                <div v-if="awsForecast6" class="aws-service-row">
                  <span class="aws-svc-name">
                    Next 6 months
                    <span class="aws-svc-raw">
                      {{ awsForecast6.prediction_interval_pct }}% interval
                    </span>
                  </span>
                  <span class="aws-svc-amt">
                    ~${{ awsForecast6.client_mean_with_unbudgeted_usd || awsForecast6.client_mean_usd }}
                    <span class="aws-svc-raw">
                      (AWS ~${{ awsForecast6.client_mean_usd }}
                      <template v-if="awsPlatformSummary.unbudgeted_pct">
                        + {{ awsPlatformSummary.unbudgeted_pct }}% buffer
                      </template>)
                    </span>
                  </span>
                </div>
                <div v-if="awsForecast12" class="aws-service-row">
                  <span class="aws-svc-name">
                    Next 12 months
                    <span class="aws-svc-raw">
                      {{ awsForecast12.prediction_interval_pct }}% interval
                    </span>
                  </span>
                  <span class="aws-svc-amt">
                    ~${{ awsForecast12.client_mean_with_unbudgeted_usd || awsForecast12.client_mean_usd }}
                    <span class="aws-svc-raw">
                      (AWS ~${{ awsForecast12.client_mean_usd }}
                      <template v-if="awsPlatformSummary.unbudgeted_pct">
                        + {{ awsPlatformSummary.unbudgeted_pct }}% buffer
                      </template>)
                    </span>
                  </span>
                </div>
                <p class="aws-forecast-hint">
                  Account forecast × this period’s
                  {{ awsSharePct }}% {{ awsPlatformSummary.client_organization_name }} share,
                  plus buffer for new services / clients / traffic.
                  Not added to this invoice’s charges.
                </p>
              </div>

              <div class="aws-actions">
                <button
                  class="btn-sm btn-sm-primary"
                  @click="addAwsPlatformItems"
                  :disabled="!awsSelectedBillableItems.length || awsPeriodStale"
                >
                  Add {{ awsPlatformSummary.client_organization_name }} AWS Items
                  ({{ awsSelectedBillableItems.length }})
                </button>
                <button
                  v-if="awsPlatformSummary.forecast?.invoice_notes_text"
                  class="btn-sm btn-sm-secondary"
                  @click="addAwsBudgetNote"
                >
                  Add 6/12-mo budget note
                </button>
              </div>
            </div>

            <div v-else-if="!loadingAws && awsFetched" class="no-data">
              No AWS platform costs found for this period.
            </div>
          </div>

          <!-- GCP Platform Infrastructure Panel -->
          <div class="import-section">
            <div class="import-section-header">
              <div class="import-label">
                <i class="fab fa-google"></i>
                GCP Platform Share
              </div>
              <button class="btn-import" @click="fetchGcpPlatform" :disabled="loadingGcp">
                <i :class="loadingGcp ? 'fas fa-spinner fa-spin' : 'fas fa-sync'"></i>
                {{ loadingGcp ? 'Fetching...' : 'Fetch GCP Costs' }}
              </button>
            </div>

            <div class="aws-split-row">
              <label class="aws-split-label">GCP bill total (USD)</label>
              <input
                v-model="gcpAccountTotal"
                type="number"
                min="0"
                step="0.01"
                class="form-control aws-split-select"
                placeholder="optional override"
              />
            </div>
            <p class="aws-forecast-hint" style="border-top: none; padding-top: 0;">
              Allocates by GCP project (like AWS tags):
              <code>accessiq-prod</code> → AccessIQ 100%;
              <code>gen-lang-client-…</code> → Praetorian (not billed here).
              July 2026 file/seed: AccessIQ <strong>$72.06</strong>
              of account ~<strong>$483.90</strong>.
              Pulled live from the Cloud Billing BigQuery export, per month.
              The export is not retroactive, so months from before it was
              enabled come from an archived
              <code>data/gcp_cost_tables/YYYY-MM.csv</code>. Pasting a Cost
              table below overrides both. Check <em>source</em> below to see
              which one answered.
            </p>
            <textarea
              v-model="gcpCsvText"
              class="form-control gcp-csv-paste"
              rows="3"
              placeholder="Optional: paste Google Cloud Cost table CSV for this period (overrides file/seed)"
            ></textarea>

            <div v-if="gcpError" class="import-error">{{ gcpError }}</div>
            <div v-if="gcpPeriodStale" class="import-error">
              Billing period changed since the last GCP fetch
              (was {{ gcpFetchedFor.start }} → {{ gcpFetchedFor.end }}).
              Fetch again before adding items.
            </div>
            <div v-if="gcpLastAddedNote" class="aws-added-note">
              {{ gcpLastAddedNote }}
            </div>

            <div v-if="gcpPlatformSummary" class="aws-summary">
              <div class="aws-fetched-period">
                Showing GCP bill for
                <strong>{{ gcpPlatformSummary.period_label }}</strong>
                ({{ gcpPlatformSummary.period_start }} → {{ gcpPlatformSummary.period_end }})
                · source {{ gcpPlatformSummary.data_source }}
              </div>
              <div
                class="aws-reconcile"
                :class="gcpPlatformSummary.reconciliation_ok ? 'ok' : 'bad'"
              >
                <i
                  :class="gcpPlatformSummary.reconciliation_ok
                    ? 'fas fa-check-circle'
                    : 'fas fa-exclamation-triangle'"
                ></i>
                <div>
                  <strong>
                    {{ gcpPlatformSummary.client_organization_name }}:
                    ${{ gcpPlatformSummary.client_share_usd }}
                  </strong>
                  +
                  <strong>
                    {{ gcpPlatformSummary.issuer_display_name }}:
                    ${{ gcpPlatformSummary.issuer_share_usd }}
                  </strong>
                  =
                  <strong>Account ${{ gcpPlatformSummary.account_total_usd }}</strong>
                </div>
              </div>

              <div class="usage-stats aws-stats">
                <span>
                  Dedicated: <strong>${{ gcpPlatformSummary.dedicated_usd }}</strong>
                </span>
                <span>
                  Issuer dedicated:
                  <strong>${{ gcpPlatformSummary.issuer_dedicated_usd }}</strong>
                </span>
                <span class="usage-cost">
                  Bill this client: ${{ gcpSelectedBillableTotal.toFixed(2) }}
                  <span class="aws-svc-raw">
                    ({{ gcpSelectedKeys.length }} lines)
                  </span>
                </span>
              </div>

              <div
                v-if="gcpDedicatedServices.length"
                class="aws-service-group"
              >
                <div class="aws-service-group-title">
                  <label class="aws-select-all">
                    <input
                      type="checkbox"
                      :checked="gcpGroupAllSelected(gcpDedicatedServices)"
                      @change="toggleGcpGroup(gcpDedicatedServices, $event.target.checked)"
                    />
                    {{ gcpPlatformSummary.client_organization_name }} dedicated
                    ({{ (gcpPlatformSummary.client_project_ids || []).join(', ') }})
                  </label>
                </div>
                <div
                  v-for="(svc, idx) in gcpDedicatedServices"
                  :key="'gd-' + idx"
                  class="aws-service-row"
                  :class="{ 'aws-row-off': !gcpSelectedKeys.includes(svc.selection_key) }"
                >
                  <label class="aws-svc-check">
                    <input
                      type="checkbox"
                      :value="svc.selection_key"
                      v-model="gcpSelectedKeys"
                    />
                    <span class="aws-svc-name">
                      <span class="aws-tag">[{{ svc.project }}]</span>
                      {{ svc.service_label }}
                    </span>
                  </label>
                  <span class="aws-svc-amt">
                    ${{ svc.invoice_amount_usd || svc.client_amount_usd }}
                  </span>
                </div>
              </div>

              <div
                v-if="gcpSharedServices.length"
                class="aws-service-group"
              >
                <div class="aws-service-group-title">
                  <label class="aws-select-all">
                    <input
                      type="checkbox"
                      :checked="gcpGroupAllSelected(gcpSharedServices)"
                      @change="toggleGcpGroup(gcpSharedServices, $event.target.checked)"
                    />
                    Unassigned projects ({{ gcpPlatformSummary.shared_split_pct }}%)
                  </label>
                </div>
                <div
                  v-for="(svc, idx) in gcpSharedServices"
                  :key="'g-' + idx"
                  class="aws-service-row"
                  :class="{ 'aws-row-off': !gcpSelectedKeys.includes(svc.selection_key) }"
                >
                  <label class="aws-svc-check">
                    <input
                      type="checkbox"
                      :value="svc.selection_key"
                      v-model="gcpSelectedKeys"
                    />
                    <span class="aws-svc-name">
                      <span class="aws-tag">[{{ svc.project }}]</span>
                      {{ svc.service_label }}
                      <span class="aws-svc-raw">${{ svc.raw_amount_usd }} pool</span>
                    </span>
                  </label>
                  <span class="aws-svc-amt">
                    ${{ svc.invoice_amount_usd || svc.client_amount_usd }}
                  </span>
                </div>
              </div>

              <div
                v-if="gcpIssuerServices.length"
                class="aws-service-group aws-issuer"
              >
                <div class="aws-service-group-title">
                  {{ gcpPlatformSummary.issuer_display_name }} dedicated
                  (never billed on this {{ gcpPlatformSummary.client_organization_name }} invoice)
                </div>
                <div
                  v-for="(svc, idx) in gcpIssuerServices"
                  :key="'gi-' + idx"
                  class="aws-service-row aws-row-excluded"
                >
                  <span class="aws-svc-name">
                    <span class="aws-tag">[{{ svc.project }}]</span>
                    {{ svc.service_label }}
                  </span>
                  <span class="aws-svc-amt">${{ svc.issuer_amount_usd }}</span>
                </div>
              </div>

              <p class="aws-methodology">{{ gcpPlatformSummary.methodology }}</p>

              <div class="aws-actions">
                <button
                  class="btn-sm btn-sm-primary"
                  @click="addGcpPlatformItems"
                  :disabled="!gcpSelectedBillableItems.length || gcpPeriodStale"
                >
                  Add {{ gcpPlatformSummary.client_organization_name }} GCP Items
                  ({{ gcpSelectedBillableItems.length }})
                </button>
              </div>
            </div>

            <div v-else-if="!loadingGcp && gcpFetched" class="no-data">
              No GCP platform costs found for this period.
            </div>
          </div>

          <InvoiceSourceDocumentsPanel
            :invoice-id="editorInvoiceId"
            :import-ready="Boolean(form.client_organization && form.billing_period_start && form.billing_period_end)"
            :ensure-draft-saved="ensureDraftSaved"
            @lines-added="onSourceLinesAdded"
            @draft-saved="onDraftSavedFromUpload"
          />
        </div>
        <div class="card import-card disabled-import" v-else>
          <div class="import-hint">
            <i class="fas fa-info-circle"></i>
            Select a client and billing period to enable data imports.
          </div>
        </div>
      </div>

      <!-- Right Column: Line Items -->
      <div class="lineitems-column">
        <div class="card">
          <div class="line-items-header">
            <h3 class="card-title"><i class="fas fa-list"></i> Line Items</h3>
            <button class="btn btn-sm-add" @click="addManualItem">
              <i class="fas fa-plus"></i> Add Item
            </button>
          </div>

          <!-- Line items list -->
          <div v-if="form.line_items.length > 0" class="line-items-list">
            <div
              v-for="(item, idx) in form.line_items"
              :key="idx"
              class="line-item-row"
              :class="`item-type-${item.line_type}`"
            >
              <div class="item-type-tag">
                <span class="type-badge" :class="`badge-${item.line_type}`">
                  {{ lineTypeLabel(item.line_type) }}
                </span>
              </div>
              <div class="item-fields">
                <div class="item-description-row">
                  <textarea
                    v-model="item.description"
                    class="item-description"
                    rows="2"
                    placeholder="Description..."
                  ></textarea>
                  <button class="remove-btn" @click="removeItem(idx)" title="Remove">
                    <i class="fas fa-times"></i>
                  </button>
                </div>
                <div class="item-amounts-row">
                  <div class="amount-group">
                    <label>Qty</label>
                    <input v-model="item.quantity" type="number" min="0" step="0.01" class="amount-input" @change="recalcItem(item)" />
                  </div>
                  <div class="amount-group">
                    <label>Unit Price</label>
                    <input v-model="item.unit_price" type="number" min="0" step="0.01" class="amount-input" @change="recalcItem(item)" />
                  </div>
                  <div class="amount-group">
                    <label>Amount</label>
                    <input v-model="item.amount" type="number" min="0" step="0.01" class="amount-input amount-total" />
                  </div>
                </div>
                <div v-if="item.line_type === 'HOURLY'" class="item-type-select-row">
                  <select v-model="item.line_type" class="type-select">
                    <option v-for="t in lineTypes" :key="t.value" :value="t.value">{{ t.label }}</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          <div v-else class="no-items">
            <i class="fas fa-plus-circle"></i>
            <p>No line items yet. Import from GitHub, usage data, AWS platform share, or add manually.</p>
          </div>

          <!-- Totals -->
          <div class="totals-section">
            <div class="totals-row">
              <span>Subtotal</span>
              <span>${{ computedSubtotal.toFixed(2) }}</span>
            </div>
            <div class="totals-row" v-if="parsedTaxRate > 0">
              <span>Tax ({{ form.tax_rate_pct }}%)</span>
              <span>${{ computedTax.toFixed(2) }}</span>
            </div>
            <div class="totals-row grand-total">
              <span>Total Due</span>
              <strong>{{ form.currency }} ${{ computedTotal.toFixed(2) }}</strong>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api';
import auth from '@/services/auth';
import InvoiceSourceDocumentsPanel from '@/components/invoicing/InvoiceSourceDocumentsPanel.vue';

export default {
  name: 'InvoiceEditor',

  components: {
    InvoiceSourceDocumentsPanel,
  },

  data() {
    const today = new Date().toISOString().split('T')[0];
    const firstOfMonth = today.substring(0, 8) + '01';
    let savedPeriod = null;
    try {
      savedPeriod = JSON.parse(sessionStorage.getItem('invoice_editor_billing_period') || 'null');
    } catch {
      savedPeriod = null;
    }
    return {
      saving: null,
      isEditing: false,
      draftInvoiceId: null,
      clients: [],
      form: {
        client_organization: '',
        client_organization_name: '',
        invoice_number: '',
        status: 'DRAFT',
        issue_date: today,
        due_date: '',
        billing_period_start: savedPeriod?.start || firstOfMonth,
        billing_period_end: savedPeriod?.end || today,
        tax_rate_pct: '0',
        currency: 'USD',
        notes: '',
        from_name: '',
        from_email: '',
        from_address: '',
        line_items: [],
      },
      // GitHub
      availableCommits: [],
      selectedCommits: [],
      selectedContributorKeys: [],
      loadingGitHub: false,
      githubError: '',
      githubFetched: false,
      githubNoMappings: false,
      loadingSummarize: false,
      summarizeError: '',
      aiSummaryItems: [],
      // Token usage
      tokenUsageSummary: null,
      loadingTokens: false,
      tokenError: '',
      tokensFetched: false,
      // Subscription
      subscriptionItem: null,
      loadingSubscription: false,
      subscriptionError: '',
      subscriptionFetched: false,
      // AWS platform infrastructure
      awsPlatformSummary: null,
      loadingAws: false,
      awsError: '',
      awsFetched: false,
      sharedSplitRatio: '0.50',
      unbudgetedRatio: '0.20',
      awsSelectedKeys: [],
      awsFetchedFor: null,
      awsLastAddedNote: '',
      // GCP platform infrastructure (Vertex / Gemini bill)
      gcpPlatformSummary: null,
      loadingGcp: false,
      gcpError: '',
      gcpFetched: false,
      gcpAccountTotal: '',
      gcpClientSplitRatio: '0.00',
      gcpCsvText: '',
      gcpSelectedKeys: [],
      gcpFetchedFor: null,
      gcpLastAddedNote: '',
      lineTypes: [
        { value: 'FLAT_FEE', label: 'Flat Fee' },
        { value: 'HOURLY', label: 'Hourly' },
        { value: 'EXPENSE', label: 'Expense' },
        { value: 'GITHUB_COMMIT', label: 'Commit' },
        { value: 'TOKEN_USAGE', label: 'Token Usage' },
        { value: 'SUBSCRIPTION', label: 'Subscription' },
      ],
    };
  },

  computed: {
    editorInvoiceId() {
      if (this.isEditing && this.$route.params.id) {
        return Number(this.$route.params.id);
      }
      return this.draftInvoiceId;
    },
    parsedTaxRate() {
      return parseFloat(this.form.tax_rate_pct || 0) / 100;
    },
    computedSubtotal() {
      return this.form.line_items.reduce((s, i) => s + parseFloat(i.amount || 0), 0);
    },
    computedTax() {
      return this.computedSubtotal * this.parsedTaxRate;
    },
    computedTotal() {
      return this.computedSubtotal + this.computedTax;
    },
    allCommitsSelected() {
      if (!this.filteredCommits.length) return false;
      const selected = new Set(this.selectedCommits);
      return this.filteredCommits.every((commit) => selected.has(commit.sha));
    },
    availableContributors() {
      const contributors = new Map();
      this.availableCommits.forEach((commit) => {
        const key = this.commitContributorKey(commit);
        const existing = contributors.get(key);
        if (existing) {
          existing.count += 1;
          return;
        }
        contributors.set(key, {
          key,
          label: this.commitContributorLabel(commit),
          count: 1,
          excludedByDefault: this.isMaryContributor(commit),
        });
      });
      return [...contributors.values()].sort((a, b) => a.label.localeCompare(b.label));
    },
    filteredCommits() {
      const included = new Set(this.selectedContributorKeys);
      return this.availableCommits.filter(
        (commit) => included.has(this.commitContributorKey(commit)),
      );
    },
    excludedContributorCommitCount() {
      return this.availableCommits.length - this.filteredCommits.length;
    },
    awsDedicatedServices() {
      return (this.awsPlatformSummary?.services || []).filter(
        (s) => s.category === 'client_dedicated',
      );
    },
    awsIssuerServices() {
      return (this.awsPlatformSummary?.services || []).filter(
        (s) => s.category === 'issuer_dedicated',
      );
    },
    awsSharedServices() {
      return (this.awsPlatformSummary?.services || []).filter((s) => s.category === 'shared');
    },
    awsUntaggedServices() {
      return (this.awsPlatformSummary?.services || []).filter((s) => s.category === 'untagged');
    },
    awsSelectableServices() {
      return (this.awsPlatformSummary?.services || []).filter((s) => s.selectable);
    },
    awsSelectedBillableItems() {
      const selected = new Set(this.awsSelectedKeys);
      return this.awsSelectableServices
        .filter((s) => selected.has(s.selection_key))
        .map((s) => {
          const amount = s.invoice_amount_usd || s.client_amount_usd || s.amount_usd;
          const splitPct = this.awsPlatformSummary?.shared_split_pct;
          let description;
          if (s.category === 'client_dedicated') {
            description = `AWS ${this.awsPlatformSummary.client_organization_name} dedicated — ${s.service_label} (${this.awsPlatformSummary.period_label})`;
          } else if (s.category === 'shared') {
            description = `AWS shared platform (${splitPct}%) — ${s.service_label} ($${s.raw_amount_usd} × ${splitPct}%) (${this.awsPlatformSummary.period_label})`;
          } else {
            description = `AWS untagged → ${this.awsPlatformSummary.client_organization_name} (100%) — ${s.service_label} ($${s.raw_amount_usd}) (${this.awsPlatformSummary.period_label})`;
          }
          return {
            line_type: 'EXPENSE',
            description,
            quantity: '1',
            unit_price: amount,
            amount,
            aws_project: s.project,
            aws_service: s.service,
            aws_selection_key: s.selection_key,
          };
        });
    },
    awsSelectedSharedTotal() {
      const selected = new Set(this.awsSelectedKeys);
      return this.awsSharedServices
        .filter((s) => selected.has(s.selection_key))
        .reduce((sum, s) => sum + parseFloat(s.invoice_amount_usd || s.client_amount_usd || 0), 0);
    },
    awsSelectedUntaggedTotal() {
      const selected = new Set(this.awsSelectedKeys);
      return this.awsUntaggedServices
        .filter((s) => selected.has(s.selection_key))
        .reduce((sum, s) => sum + parseFloat(s.invoice_amount_usd || s.raw_amount_usd || 0), 0);
    },
    awsSelectedBillableTotal() {
      return this.awsSelectedBillableItems.reduce(
        (sum, li) => sum + parseFloat(li.amount || 0),
        0,
      );
    },
    awsForecast6() {
      const h = this.awsPlatformSummary?.forecast?.horizons?.['6'];
      return h?.available ? h : null;
    },
    awsForecast12() {
      const h = this.awsPlatformSummary?.forecast?.horizons?.['12'];
      return h?.available ? h : null;
    },
    awsSharePct() {
      const ratio = parseFloat(this.awsPlatformSummary?.client_share_ratio || 0);
      if (!ratio) return '—';
      return (ratio * 100).toFixed(1);
    },
    awsPeriodStale() {
      if (!this.awsFetchedFor || !this.awsPlatformSummary) return false;
      return (
        this.awsFetchedFor.start !== this.form.billing_period_start
        || this.awsFetchedFor.end !== this.form.billing_period_end
      );
    },
    gcpSharedServices() {
      return (this.gcpPlatformSummary?.services || []).filter((s) => s.category === 'shared');
    },
    gcpDedicatedServices() {
      return (this.gcpPlatformSummary?.services || []).filter(
        (s) => s.category === 'client_dedicated',
      );
    },
    gcpIssuerServices() {
      return (this.gcpPlatformSummary?.services || []).filter(
        (s) => s.category === 'issuer_dedicated',
      );
    },
    gcpSelectableServices() {
      return (this.gcpPlatformSummary?.services || []).filter((s) => s.selectable);
    },
    gcpSelectedBillableItems() {
      const selected = new Set(this.gcpSelectedKeys);
      const periodLabel = this.gcpPlatformSummary?.period_label;
      const clientName = this.gcpPlatformSummary?.client_organization_name;
      return this.gcpSelectableServices
        .filter((s) => selected.has(s.selection_key))
        .map((s) => {
          const amount = s.invoice_amount_usd || s.client_amount_usd || s.amount_usd;
          let description;
          if (s.category === 'client_dedicated') {
            description = (
              `GCP ${clientName} dedicated (${s.project}) — `
              + `${s.service_label} (${periodLabel})`
            );
          } else {
            const splitPct = this.gcpPlatformSummary?.shared_split_pct;
            description = (
              `GCP unassigned project ${s.project} (${splitPct}%) — `
              + `${s.service_label} ($${s.raw_amount_usd} × ${splitPct}%) (${periodLabel})`
            );
          }
          return {
            line_type: 'EXPENSE',
            description,
            quantity: '1',
            unit_price: String(amount),
            amount: String(amount),
            gcp_project: s.project,
            gcp_service: s.service,
            gcp_selection_key: s.selection_key,
          };
        });
    },
    gcpSelectedSharedTotal() {
      return this.gcpSelectedBillableItems.reduce(
        (sum, li) => sum + parseFloat(li.amount || 0),
        0,
      );
    },
    gcpSelectedBillableTotal() {
      return this.gcpSelectedSharedTotal;
    },
    gcpPeriodStale() {
      if (!this.gcpFetchedFor || !this.gcpPlatformSummary) return false;
      return (
        this.gcpFetchedFor.start !== this.form.billing_period_start
        || this.gcpFetchedFor.end !== this.form.billing_period_end
      );
    },
  },

  watch: {
    'form.billing_period_start'(val, oldVal) {
      if (val === oldVal) return;
      this.onBillingPeriodChange();
    },
    'form.billing_period_end'(val, oldVal) {
      if (val === oldVal) return;
      this.onBillingPeriodChange();
    },
  },

  async mounted() {
    await this.loadClients();
    const invoiceId = this.$route.params.id;
    if (invoiceId) {
      this.isEditing = true;
      await this.loadInvoice(invoiceId);
    } else {
      const org = auth.getUserOrganization();
      if (org?.name) this.form.from_name = org.name;
    }
  },

  methods: {
    async loadClients() {
      try {
        const res = await api.get('/invoicing/clients/');
        this.clients = res.data;
      } catch (e) {
        console.error('Failed to load clients', e);
      }
    },

    async loadInvoice(id) {
      try {
        const res = await api.get(`/invoices/${id}/`);
        const inv = res.data;
        this.form = {
          client_organization: inv.client_organization,
          client_organization_name: inv.client_organization_name,
          invoice_number: inv.invoice_number,
          status: inv.status,
          issue_date: inv.issue_date,
          due_date: inv.due_date || '',
          billing_period_start: inv.billing_period_start || '',
          billing_period_end: inv.billing_period_end || '',
          tax_rate_pct: inv.tax_rate ? (parseFloat(inv.tax_rate) * 100).toFixed(2) : '0',
          currency: inv.currency,
          notes: inv.notes || '',
          from_name: inv.from_name || inv.issuer_organization_name || '',
          from_email: inv.from_email || '',
          from_address: inv.from_address || '',
          line_items: inv.line_items.map((li) => ({ ...li })),
        };
      } catch (e) {
        alert('Failed to load invoice');
        this.$router.push('/invoicing');
      }
    },

    onClientChange() {
      const client = this.clients.find((c) => c.id === this.form.client_organization);
      this.form.client_organization_name = client?.name || '';
      this.resetImports();
    },

    resetImports() {
      this.availableCommits = [];
      this.selectedCommits = [];
      this.selectedContributorKeys = [];
      this.githubFetched = false;
      this.githubNoMappings = false;
      this.tokenUsageSummary = null;
      this.tokensFetched = false;
      this.subscriptionItem = null;
      this.subscriptionFetched = false;
      this.awsPlatformSummary = null;
      this.awsSelectedKeys = [];
      this.awsFetchedFor = null;
      this.awsFetched = false;
      this.awsError = '';
      this.awsLastAddedNote = '';
      this.gcpPlatformSummary = null;
      this.gcpSelectedKeys = [];
      this.gcpFetchedFor = null;
      this.gcpFetched = false;
      this.gcpError = '';
      this.gcpLastAddedNote = '';
    },

    persistBillingPeriod() {
      const start = this.form.billing_period_start;
      const end = this.form.billing_period_end;
      if (!start || !end) return;
      try {
        sessionStorage.setItem(
          'invoice_editor_billing_period',
          JSON.stringify({ start, end }),
        );
      } catch {
        /* ignore quota / private mode */
      }
    },

    onBillingPeriodChange() {
      this.persistBillingPeriod();
      // Drop stale CE results so Add can't use July data under June dates
      if (this.awsPlatformSummary || this.awsFetchedFor) {
        this.awsPlatformSummary = null;
        this.awsSelectedKeys = [];
        this.awsFetchedFor = null;
        this.awsFetched = false;
        this.awsError = '';
      }
      if (this.gcpPlatformSummary || this.gcpFetchedFor) {
        this.gcpPlatformSummary = null;
        this.gcpSelectedKeys = [];
        this.gcpFetchedFor = null;
        this.gcpFetched = false;
        this.gcpError = '';
      }
    },

    async fetchGitHubCommits() {
      this.loadingGitHub = true;
      this.githubError = '';
      this.githubNoMappings = false;
      this.availableCommits = [];
      try {
        const res = await api.post('/github-audit/commits/', {
          client_organization_id: this.form.client_organization,
          since: this.form.billing_period_start,
          until: this.form.billing_period_end,
        });
        if (res.data.no_mappings) {
          this.githubNoMappings = true;
        } else {
          this.availableCommits = res.data.commits || [];
          const preferredCommits = this.availableCommits.filter(
            (commit) => this.isPreferredInvoiceContributor(commit),
          );
          const defaultCommits = preferredCommits.length
            ? preferredCommits
            : this.availableCommits.filter((commit) => !this.isMaryContributor(commit));
          this.selectedContributorKeys = [
            ...new Set(
              defaultCommits.map((commit) => this.commitContributorKey(commit)),
            ),
          ];
          if (this.availableCommits.length === 0 && res.data.debug?.length) {
            const info = res.data.debug.map((d) =>
              d.error
                ? `${d.repo}: error — ${d.error}`
                : `${d.repo}: 0 commits between ${d.since} and ${d.until}`
            ).join('\n');
            this.githubError = `No commits found.\n\nQueried:\n${info}\n\nCheck that the billing period dates are correct and the token has repo read access.`;
          }
        }
        this.githubFetched = true;
      } catch (e) {
        this.githubError = e.response?.data?.detail || 'Failed to fetch commits. Check your GitHub token in API Integrations.';
      } finally {
        this.loadingGitHub = false;
      }
    },

    async fetchTokenUsage() {
      this.loadingTokens = true;
      this.tokenError = '';
      this.tokenUsageSummary = null;
      try {
        const res = await api.get('/usage-audit/tokens/', {
          params: {
            client_org_id: this.form.client_organization,
            since: this.form.billing_period_start,
            until: this.form.billing_period_end,
          },
        });
        this.tokenUsageSummary = res.data;
        this.tokensFetched = true;
      } catch (e) {
        this.tokenError = e.response?.data?.detail || 'Failed to fetch token usage.';
      } finally {
        this.loadingTokens = false;
      }
    },

    async fetchSubscription() {
      this.loadingSubscription = true;
      this.subscriptionError = '';
      this.subscriptionItem = null;
      try {
        const res = await api.get('/usage-audit/subscription/', {
          params: { client_org_id: this.form.client_organization },
        });
        this.subscriptionItem = res.data.line_item;
        this.subscriptionFetched = true;
      } catch (e) {
        this.subscriptionError = e.response?.data?.detail || 'Failed to fetch subscription.';
      } finally {
        this.loadingSubscription = false;
      }
    },

    async fetchAwsPlatform() {
      this.loadingAws = true;
      this.awsError = '';
      this.awsPlatformSummary = null;
      this.awsSelectedKeys = [];
      this.awsLastAddedNote = '';
      this.persistBillingPeriod();
      try {
        const res = await api.get('/usage-audit/aws-platform/', {
          params: {
            client_org_id: this.form.client_organization,
            since: this.form.billing_period_start,
            until: this.form.billing_period_end,
            shared_split_ratio: this.sharedSplitRatio,
            unbudgeted_ratio: this.unbudgetedRatio,
          },
        });
        this.awsPlatformSummary = res.data;
        this.awsFetchedFor = {
          start: this.form.billing_period_start,
          end: this.form.billing_period_end,
        };
        this.awsSelectedKeys = (res.data.services || [])
          .filter((s) => s.selectable && s.selected_default)
          .map((s) => s.selection_key);
        this.awsFetched = true;
      } catch (e) {
        const detail = e.response?.data?.detail;
        this.awsError = detail || 'Failed to fetch AWS platform costs.';
        this.awsFetchedFor = null;
      } finally {
        this.loadingAws = false;
      }
    },

    awsGroupAllSelected(services) {
      if (!services?.length) return false;
      return services.every((s) => this.awsSelectedKeys.includes(s.selection_key));
    },

    toggleAwsGroup(services, checked) {
      const keys = new Set(this.awsSelectedKeys);
      services.forEach((s) => {
        if (!s.selection_key) return;
        if (checked) keys.add(s.selection_key);
        else keys.delete(s.selection_key);
      });
      this.awsSelectedKeys = [...keys];
    },

    addAwsPlatformItems() {
      if (this.awsPeriodStale) {
        this.awsError = 'Billing period changed — fetch AWS costs again before adding.';
        return;
      }
      const items = this.awsSelectedBillableItems;
      if (!items.length) return;
      const periodLabel = this.awsPlatformSummary?.period_label
        || `${this.form.billing_period_start} → ${this.form.billing_period_end}`;
      this.form.line_items.push(...items.map((li) => ({ ...li })));
      this.awsLastAddedNote = `Added ${items.length} AWS line(s) for ${periodLabel}.`;
      this.awsPlatformSummary = null;
      this.awsSelectedKeys = [];
      // Keep awsFetchedFor so the note/period context stays visible
    },

    addAwsBudgetNote() {
      const text = this.awsPlatformSummary?.forecast?.invoice_notes_text;
      if (!text) return;
      const existing = (this.form.notes || '').trim();
      if (existing.includes('AWS budget estimate')) {
        return;
      }
      this.form.notes = existing ? `${existing}\n\n${text}` : text;
    },

    async fetchGcpPlatform() {
      this.loadingGcp = true;
      this.gcpError = '';
      this.gcpPlatformSummary = null;
      this.gcpSelectedKeys = [];
      this.gcpLastAddedNote = '';
      this.persistBillingPeriod();
      try {
        const payload = {
          client_org_id: this.form.client_organization,
          since: this.form.billing_period_start,
          until: this.form.billing_period_end,
          client_split_ratio: this.gcpClientSplitRatio,
        };
        const total = String(this.gcpAccountTotal || '').trim();
        if (total) {
          payload.account_total = total;
        }
        const csvText = String(this.gcpCsvText || '').trim();
        let res;
        if (csvText) {
          payload.csv_text = csvText;
          res = await api.post('/usage-audit/gcp-platform/', payload);
        } else {
          res = await api.get('/usage-audit/gcp-platform/', { params: payload });
        }
        this.gcpPlatformSummary = res.data;
        this.gcpFetchedFor = {
          start: this.form.billing_period_start,
          end: this.form.billing_period_end,
        };
        this.gcpSelectedKeys = (res.data.services || [])
          .filter((s) => s.selectable && s.selected_default)
          .map((s) => s.selection_key);
        this.gcpFetched = true;
        if (!total && res.data.account_total_usd) {
          this.gcpAccountTotal = res.data.account_total_usd;
        }
      } catch (e) {
        const detail = e.response?.data?.detail;
        this.gcpError = detail || 'Failed to fetch GCP platform costs.';
        this.gcpFetchedFor = null;
      } finally {
        this.loadingGcp = false;
      }
    },

    gcpGroupAllSelected(services) {
      if (!services?.length) return false;
      return services.every((s) => this.gcpSelectedKeys.includes(s.selection_key));
    },

    toggleGcpGroup(services, checked) {
      const keys = new Set(this.gcpSelectedKeys);
      services.forEach((s) => {
        if (!s.selection_key) return;
        if (checked) keys.add(s.selection_key);
        else keys.delete(s.selection_key);
      });
      this.gcpSelectedKeys = [...keys];
    },

    addGcpPlatformItems() {
      if (this.gcpPeriodStale) {
        this.gcpError = 'Billing period changed — fetch GCP costs again before adding.';
        return;
      }
      const items = this.gcpSelectedBillableItems;
      if (!items.length) return;
      const periodLabel = this.gcpPlatformSummary?.period_label
        || `${this.form.billing_period_start} → ${this.form.billing_period_end}`;
      this.form.line_items.push(...items.map((li) => ({ ...li })));
      this.gcpLastAddedNote = `Added ${items.length} GCP line(s) for ${periodLabel}.`;
      this.gcpPlatformSummary = null;
      this.gcpSelectedKeys = [];
    },

    toggleAllCommits(e) {
      const visibleShas = new Set(this.filteredCommits.map((commit) => commit.sha));
      if (e.target.checked) {
        this.selectedCommits = [
          ...new Set([...this.selectedCommits, ...visibleShas]),
        ];
      } else {
        this.selectedCommits = this.selectedCommits.filter(
          (sha) => !visibleShas.has(sha),
        );
      }
    },

    commitContributorKey(commit) {
      return String(
        commit.author_login
        || commit.author_email
        || commit.author_name
        || 'unknown-author',
      ).trim().toLowerCase();
    },

    commitContributorLabel(commit) {
      const name = String(commit.author_name || '').trim();
      const login = String(commit.author_login || '').trim();
      if (name && login && name.toLowerCase() !== login.toLowerCase()) {
        return `${name} (@${login})`;
      }
      return name || (login ? `@${login}` : '') || commit.author_email || 'Unknown author';
    },

    isMaryContributor(commit) {
      const identity = [
        commit.author_name,
        commit.author_login,
        commit.author_email,
      ].filter(Boolean).join(' ').toLowerCase();
      return /(^|[\s._@-])mary([\s._@-]|$)/.test(identity);
    },

    isPreferredInvoiceContributor(commit) {
      const identity = [
        commit.author_name,
        commit.author_login,
        commit.author_email,
      ].filter(Boolean).join(' ').toLowerCase();
      return /\b(brad|govardin|praetorianai)\b/.test(identity);
    },

    toggleContributor(key, checked) {
      const keys = new Set(this.selectedContributorKeys);
      if (checked) keys.add(key);
      else keys.delete(key);
      this.selectedContributorKeys = [...keys];

      if (!checked) {
        const excludedShas = new Set(
          this.availableCommits
            .filter((commit) => this.commitContributorKey(commit) === key)
            .map((commit) => commit.sha),
        );
        this.selectedCommits = this.selectedCommits.filter(
          (sha) => !excludedShas.has(sha),
        );
      }
    },

    addSelectedCommits() {
      const selectedSet = new Set(this.selectedCommits);
      const existingShas = new Set(this.form.line_items.map((li) => li.github_commit_sha).filter(Boolean));

      const toAdd = this.availableCommits
        .filter((c) => selectedSet.has(c.sha) && !existingShas.has(c.sha))
        .map((c) => ({
          line_type: 'GITHUB_COMMIT',
          description: `[${c.short_sha}] ${c.repo}: ${c.message}`,
          quantity: '1',
          unit_price: '0',
          amount: '0',
          github_commit_sha: c.sha,
          github_repo: c.repo,
          commit_date: c.date?.substring(0, 10) || '',
          commit_author: c.author_name,
          commit_url: c.html_url,
        }));

      this.form.line_items.push(...toAdd);
      this.selectedCommits = [];
    },

    async summarizeCommits() {
      if (this.selectedCommits.length === 0) return;
      this.loadingSummarize = true;
      this.summarizeError = '';
      this.aiSummaryItems = [];

      const selectedSet = new Set(this.selectedCommits);
      const commits = this.availableCommits.filter((c) => selectedSet.has(c.sha));
      const clientObj = this.clients.find((c) => c.id === this.form.client_organization);

      try {
        const res = await api.post('/github-audit/summarize/', {
          commits,
          client_name: clientObj?.name || 'Client',
          billing_period_start: this.form.billing_period_start,
          billing_period_end: this.form.billing_period_end,
        });
        this.aiSummaryItems = res.data.line_items || [];
      } catch (e) {
        this.summarizeError = e.response?.data?.detail || 'AI summarization failed. Try again.';
      } finally {
        this.loadingSummarize = false;
      }
    },

    addAISummaryItems() {
      this.form.line_items.push(...this.aiSummaryItems.map((item) => ({ ...item })));
      this.aiSummaryItems = [];
      this.selectedCommits = [];
      // Scroll line items column into view
      this.$nextTick(() => {
        const el = document.querySelector('.lineitems-column');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    },

    addTokenUsageItems() {
      if (!this.tokenUsageSummary?.line_items) return;
      this.form.line_items.push(...this.tokenUsageSummary.line_items.map((li) => ({ ...li })));
      this.tokenUsageSummary = null;
    },

    addSubscriptionItem() {
      if (!this.subscriptionItem) return;
      this.form.line_items.push({ ...this.subscriptionItem });
      this.subscriptionItem = null;
    },

    addManualItem() {
      this.form.line_items.push({
        line_type: 'FLAT_FEE',
        description: '',
        quantity: '1',
        unit_price: '0',
        amount: '0',
      });
    },

    removeItem(idx) {
      this.form.line_items.splice(idx, 1);
    },

    recalcItem(item) {
      const qty = parseFloat(item.quantity || 0);
      const price = parseFloat(item.unit_price || 0);
      item.amount = (qty * price).toFixed(2);
    },

    lineTypeLabel(type) {
      const map = {
        FLAT_FEE: 'Flat',
        HOURLY: 'Hourly',
        EXPENSE: 'Expense',
        GITHUB_COMMIT: 'Commit',
        TOKEN_USAGE: 'Tokens',
        SUBSCRIPTION: 'Subscription',
      };
      return map[type] || type;
    },

    formatDateShort(dateStr) {
      if (!dateStr) return '';
      return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    },

    buildPayload(status) {
      return {
        client_organization: this.form.client_organization,
        status,
        issue_date: this.form.issue_date,
        due_date: this.form.due_date || null,
        billing_period_start: this.form.billing_period_start || null,
        billing_period_end: this.form.billing_period_end || null,
        tax_rate: (parseFloat(this.form.tax_rate_pct || 0) / 100).toFixed(4),
        currency: this.form.currency,
        notes: this.form.notes,
        from_name: this.form.from_name || '',
        from_email: this.form.from_email || '',
        from_address: this.form.from_address || '',
        line_items: this.form.line_items.map((li, i) => ({
          ...li,
          sort_order: i,
        })),
      };
    },

    formatSaveError(data) {
      if (!data) return 'Could not save the invoice.';
      if (typeof data === 'string') return data;
      if (typeof data.detail === 'string') return data.detail;

      const labels = {
        description: 'description',
        quantity: 'quantity',
        unit_price: 'unit price',
        amount: 'amount',
        line_type: 'line type',
        client_organization: 'client',
        issue_date: 'issue date',
        due_date: 'due date',
        billing_period_start: 'billing period start',
        billing_period_end: 'billing period end',
        tax_rate: 'tax rate',
        currency: 'currency',
        notes: 'notes',
        from_name: 'business name',
        from_email: 'email',
        from_address: 'address',
      };
      const labelFor = (field) => labels[field] || String(field).replace(/_/g, ' ');
      const articleFor = (name) => (/^[aeiou]/i.test(name) ? 'an' : 'a');
      const asText = (messages) => {
        if (Array.isArray(messages)) return messages.filter((msg) => typeof msg === 'string');
        if (typeof messages === 'string') return [messages];
        return [];
      };
      const lines = [];
      const pushField = (prefix, field, messages) => {
        const name = labelFor(field);
        asText(messages).forEach((msg) => {
          if (msg === 'This field may not be blank.' || msg === 'This field is required.') {
            const sentence = `Needs ${articleFor(name)} ${name}.`;
            lines.push(prefix ? `${prefix} ${sentence.charAt(0).toLowerCase()}${sentence.slice(1)}` : sentence);
            return;
          }
          lines.push(prefix ? `${prefix}: ${msg}` : msg);
        });
      };

      const items = data.line_items;
      if (items && typeof items === 'object') {
        const entries = Array.isArray(items)
          ? items.map((item, index) => [index, item])
          : Object.entries(items);
        entries.forEach(([index, item]) => {
          if (!item || typeof item !== 'object' || Array.isArray(item)) return;
          const lineLabel = `Line ${Number(index) + 1}`;
          Object.entries(item).forEach(([field, messages]) => {
            if (field === 'non_field_errors') {
              asText(messages).forEach((msg) => lines.push(`${lineLabel}: ${msg}`));
              return;
            }
            pushField(lineLabel, field, messages);
          });
        });
      }

      Object.entries(data).forEach(([field, messages]) => {
        if (field === 'line_items' || field === 'detail') return;
        if (field === 'non_field_errors') {
          asText(messages).forEach((msg) => lines.push(msg));
          return;
        }
        pushField('', field, messages);
      });

      return lines.length ? lines.join('\n') : JSON.stringify(data, null, 2);
    },

    async saveInvoice(status) {
      if (!this.form.client_organization) {
        alert('Please select a client organization.');
        return;
      }
      this.saving = status;
      try {
        const response = await this.persistInvoice(status);
        this.$router.push(`/invoicing/${response.data.id}`);
      } catch (e) {
        const detail = e.response?.data
          ? this.formatSaveError(e.response.data)
          : (e.message || 'Could not save the invoice.');
        alert('Save failed:\n' + detail);
      } finally {
        this.saving = null;
      }
    },

    async persistInvoice(status) {
      const payload = this.buildPayload(status);
      if (this.isEditing || this.draftInvoiceId) {
        const id = this.isEditing ? this.$route.params.id : this.draftInvoiceId;
        return api.put(`/invoices/${id}/`, payload);
      }
      return api.post('/invoices/', payload);
    },

    async ensureDraftSaved() {
      if (!this.form.client_organization) {
        throw new Error('Select a client organization first.');
      }
      if (this.editorInvoiceId) {
        return this.editorInvoiceId;
      }
      const response = await this.persistInvoice('DRAFT');
      this.draftInvoiceId = response.data.id;
      this.form.invoice_number = response.data.invoice_number;
      return response.data.id;
    },

    onDraftSavedFromUpload(invoiceId) {
      this.draftInvoiceId = invoiceId;
      if (!this.isEditing) {
        this.$router.replace(`/invoicing/${invoiceId}/edit`);
        this.isEditing = true;
      }
    },

    onSourceLinesAdded(lines) {
      const mapped = (lines || []).map((li) => ({
        line_type: li.line_type,
        description: li.description,
        quantity: li.quantity,
        unit_price: li.unit_price,
        amount: li.amount,
        source_document: li.source_document,
      }));
      this.form.line_items.push(...mapped);
    },
  },
};
</script>

<style scoped>
.invoice-editor-container {
  padding: 24px;
  max-width: 1400px;
  margin: 0 auto;
}

.editor-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 28px;
  gap: 16px;
}

.header-left { display: flex; align-items: flex-start; gap: 16px; }

.back-btn {
  background: none;
  border: 1px solid var(--border, #e0e4f0);
  border-radius: 8px;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 1rem;
  color: var(--text-secondary, #6c7a9c);
  transition: all 0.15s;
  flex-shrink: 0;
  margin-top: 4px;
}
.back-btn:hover { background: #f0f3ff; border-color: var(--primary, #2c3e8c); }

.page-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text-primary, #1a1a2e);
  display: flex;
  align-items: center;
  gap: 10px;
}

.invoice-num-badge {
  font-size: 0.85rem;
  font-weight: 600;
  font-family: monospace;
  background: #e8eaf6;
  color: #3949ab;
  padding: 2px 10px;
  border-radius: 12px;
}

.page-subtitle { color: var(--text-secondary, #6c7a9c); font-size: 0.875rem; margin-top: 4px; }

.header-actions { display: flex; gap: 10px; flex-shrink: 0; }

.btn {
  padding: 8px 20px;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.875rem;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: all 0.15s;
}
.btn:disabled { opacity: 0.6; cursor: not-allowed; }
.btn-primary { background: var(--primary, #2c3e8c); color: #fff; }
.btn-primary:hover:not(:disabled) { background: var(--primary-dark, #1e2d6e); }
.btn-secondary { background: #f0f3ff; color: var(--primary, #2c3e8c); }
.btn-secondary:hover:not(:disabled) { background: #e0e4f8; }

.editor-body {
  display: grid;
  grid-template-columns: 380px 1fr;
  gap: 24px;
  align-items: start;
}

@media (max-width: 900px) {
  .editor-body { grid-template-columns: 1fr; }
}

.card {
  background: #fff;
  border: 1px solid var(--border, #e0e4f0);
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 16px;
}

.card-title {
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--text-primary, #1a1a2e);
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
}
.card-title i { color: var(--primary, #2c3e8c); }

.form-group { margin-bottom: 14px; }
.form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }

label {
  display: block;
  font-size: 0.78rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--text-secondary, #6c7a9c);
  margin-bottom: 5px;
}
.required { color: #e53935; }

.form-control {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid var(--border, #e0e4f0);
  border-radius: 8px;
  font-size: 0.875rem;
  background: #fff;
  color: var(--text-primary, #1a1a2e);
  transition: border-color 0.15s;
}
.form-control:focus { outline: none; border-color: var(--primary, #2c3e8c); }
textarea.form-control { resize: vertical; }

/* Import Panel */
.import-card { padding: 16px; }
.import-section {
  border: 1px solid var(--border, #e0e4f0);
  border-radius: 10px;
  padding: 14px;
  margin-bottom: 12px;
  background: #fafbff;
}
.import-section:last-child { margin-bottom: 0; }

.import-section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.import-label {
  font-size: 0.85rem;
  font-weight: 700;
  color: var(--text-primary, #1a1a2e);
  display: flex;
  align-items: center;
  gap: 8px;
}
.import-label i { color: var(--primary, #2c3e8c); }

.btn-import {
  background: var(--primary, #2c3e8c);
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 5px 12px;
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.15s;
}
.btn-import:disabled { opacity: 0.6; cursor: not-allowed; }
.btn-import:hover:not(:disabled) { background: var(--primary-dark, #1e2d6e); }

.import-error {
  background: #fce4ec;
  color: #c62828;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 0.8rem;
  margin-top: 8px;
  white-space: pre-line;
}

.commit-list { max-height: 220px; overflow-y: auto; }
.commit-list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 0 8px;
  border-bottom: 1px solid var(--border, #e0e4f0);
  margin-bottom: 6px;
}
.check-all { font-size: 0.8rem; font-weight: 600; display: flex; align-items: center; gap: 6px; cursor: pointer; }

.commit-actions { display: flex; gap: 6px; align-items: center; }

.contributor-filter {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 14px;
  margin: 8px 0;
  padding: 8px 10px;
  border: 1px solid var(--border, #e0e4f0);
  border-radius: 6px;
  background: var(--surface-muted, #f8f9ff);
}
.contributor-filter-label {
  flex-basis: 100%;
  font-size: 0.76rem;
  font-weight: 700;
  color: var(--text-secondary, #6c7a9c);
}
.contributor-option {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 0.78rem;
  cursor: pointer;
}
.contributor-default-note { color: #8a5a00; font-size: 0.7rem; }

.btn-sm {
  padding: 4px 10px;
  border-radius: 6px;
  border: 1px solid var(--border, #e0e4f0);
  background: #fff;
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
}
.btn-sm:disabled { opacity: 0.4; cursor: not-allowed; }
.btn-sm:hover:not(:disabled) { background: #f0f3ff; }
.btn-sm-primary { background: var(--primary, #2c3e8c); color: #fff; border-color: transparent; }
.btn-sm-primary:hover:not(:disabled) { background: var(--primary-dark, #1e2d6e); }
.btn-sm-ai {
  background: linear-gradient(135deg, #7c3aed, #4f46e5);
  color: #fff;
  border-color: transparent;
}
.btn-sm-ai:hover:not(:disabled) { background: linear-gradient(135deg, #6d28d9, #4338ca); }

/* AI Summary Panel */
.ai-summary-panel {
  background: linear-gradient(135deg, #f5f3ff, #eef2ff);
  border: 1px solid #c4b5fd;
  border-radius: 10px;
  padding: 12px;
  margin: 8px 0;
}
.ai-summary-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  font-size: 0.8rem;
  font-weight: 700;
  color: #5b21b6;
}
.ai-dismiss {
  background: none;
  border: none;
  color: #a78bfa;
  cursor: pointer;
  padding: 2px 4px;
  font-size: 0.8rem;
}
.ai-dismiss:hover { color: #7c3aed; }

.ai-summary-item {
  background: #fff;
  border: 1px solid #ddd6fe;
  border-radius: 8px;
  padding: 8px 10px;
  margin-bottom: 6px;
}
.ai-item-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 5px;
}
.ai-item-type {
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: #ede9fe;
  color: #7c3aed;
  padding: 2px 7px;
  border-radius: 4px;
}
.ai-item-remove {
  background: none;
  border: none;
  color: #d1d5db;
  cursor: pointer;
  font-size: 0.75rem;
  padding: 0;
  line-height: 1;
}
.ai-item-remove:hover { color: #ef4444; }
.ai-item-edit {
  width: 100%;
  border: 1px solid #e5e7eb;
  border-radius: 5px;
  padding: 5px 7px;
  font-size: 0.82rem;
  color: var(--text-primary, #1a1a2e);
  resize: vertical;
  font-family: inherit;
  line-height: 1.4;
  box-sizing: border-box;
}
.ai-item-edit:focus { outline: none; border-color: #a78bfa; }

.btn-add-to-invoice {
  width: 100%;
  margin-top: 10px;
  padding: 10px;
  background: linear-gradient(135deg, #7c3aed, #4f46e5);
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.15s;
}
.btn-add-to-invoice:hover { background: linear-gradient(135deg, #6d28d9, #4338ca); transform: translateY(-1px); }

.commit-item { margin-bottom: 4px; }
.commit-checkbox { display: flex; align-items: flex-start; gap: 8px; cursor: pointer; padding: 4px 0; }
.commit-info { display: flex; flex-wrap: wrap; gap: 4px; align-items: baseline; }
.commit-sha { font-family: monospace; font-size: 0.75rem; background: #f3e5f5; color: #6a1b9a; padding: 1px 5px; border-radius: 3px; }
.commit-repo { font-size: 0.72rem; color: var(--text-secondary, #6c7a9c); }
.commit-date { font-size: 0.72rem; color: #888; }
.commit-author { font-size: 0.72rem; font-weight: 600; color: #3557a5; }
.commit-msg { font-size: 0.8rem; color: var(--text-primary, #1a1a2e); flex-basis: 100%; }

.usage-summary { display: flex; justify-content: space-between; align-items: center; }
.usage-stats { font-size: 0.82rem; color: var(--text-secondary, #6c7a9c); display: flex; gap: 12px; }
.usage-cost { font-weight: 700; color: var(--text-primary, #1a1a2e); }

.aws-split-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}
.aws-split-label {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: var(--text-secondary, #6c7a9c);
  margin: 0;
  white-space: nowrap;
}
.aws-split-select {
  flex: 1;
  padding: 6px 10px;
  font-size: 0.8rem;
}
.gcp-csv-paste {
  width: 100%;
  margin: 6px 0 8px;
  font-size: 0.72rem;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  resize: vertical;
  min-height: 3.5rem;
}

.aws-summary { margin-top: 4px; }
.aws-stats { flex-wrap: wrap; margin-bottom: 8px; }
.aws-methodology {
  font-size: 0.75rem;
  color: var(--text-secondary, #6c7a9c);
  line-height: 1.4;
  margin: 0 0 10px;
}
.aws-reconcile {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  border-radius: 6px;
  padding: 8px 10px;
  font-size: 0.8rem;
  margin-bottom: 10px;
  line-height: 1.4;
}
.aws-reconcile.ok {
  background: #ecfdf5;
  border: 1px solid #6ee7b7;
  color: #065f46;
}
.aws-reconcile.bad {
  background: #fef2f2;
  border: 1px solid #fca5a5;
  color: #991b1b;
}
.aws-reconcile i { margin-top: 2px; flex-shrink: 0; }

.aws-service-group {
  margin: 10px 0;
  border: 1px solid var(--border, #e0e4f0);
  border-radius: 6px;
  overflow: hidden;
}
.aws-service-group-title {
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--text-secondary, #6c7a9c);
  background: #f8f9fc;
  padding: 6px 10px;
  border-bottom: 1px solid var(--border, #e0e4f0);
}
.aws-fetched-period {
  font-size: 0.8rem;
  padding: 8px 10px;
  background: #eff6ff;
  border-bottom: 1px solid #bfdbfe;
  color: #1e3a8a;
}
.aws-added-note {
  margin: 8px 0;
  padding: 8px 10px;
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
  border-radius: 6px;
  color: #065f46;
  font-size: 0.8rem;
}
.aws-service-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 8px;
  padding: 5px 10px;
  font-size: 0.8rem;
  border-bottom: 1px solid #f0f2f7;
}
.aws-service-row:last-child { border-bottom: none; }
.aws-svc-check {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  flex: 1;
  min-width: 0;
  cursor: pointer;
  margin: 0;
}
.aws-svc-check input { margin-top: 3px; flex-shrink: 0; }
.aws-select-all {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  margin: 0;
  font: inherit;
  color: inherit;
  text-transform: inherit;
  letter-spacing: inherit;
  font-weight: inherit;
}
.aws-tag {
  color: var(--text-secondary, #6c7a9c);
  font-size: 0.72rem;
  margin-right: 4px;
}
.aws-row-excluded,
.aws-row-off {
  opacity: 0.55;
}
.aws-svc-name { color: var(--text-primary, #1a1a2e); }
.aws-svc-raw {
  color: var(--text-secondary, #6c7a9c);
  font-size: 0.72rem;
  margin-left: 6px;
}
.aws-svc-amt { font-weight: 600; white-space: nowrap; }
.aws-issuer .aws-service-group-title,
.aws-untagged .aws-service-group-title { color: #92400e; }
.aws-issuer { border-color: #fde68a; }
.aws-untagged { border-color: #fcd34d; }
.aws-forecast { border-color: #93c5fd; }
.aws-forecast .aws-service-group-title { color: #1e40af; }
.aws-forecast-buffer {
  margin: 0.5rem 0 0.75rem;
  padding: 0 0.75rem;
}
.aws-forecast-hint {
  font-size: 0.72rem;
  color: var(--text-secondary, #6c7a9c);
  padding: 6px 10px 8px;
  margin: 0;
  border-top: 1px solid #e0e7ff;
}
.aws-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 4px;
}
.btn-sm-secondary {
  background: #fff;
  border: 1px solid var(--border, #c5cce0);
  color: var(--text-primary, #1a1a2e);
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 0.8rem;
  cursor: pointer;
}
.btn-sm-secondary:hover { background: #f8f9fc; }

.subscription-item { }
.sub-description { font-size: 0.85rem; color: var(--text-primary, #1a1a2e); margin-bottom: 8px; }
.sub-footer { display: flex; justify-content: space-between; align-items: center; }

.no-data { font-size: 0.8rem; color: var(--text-secondary, #6c7a9c); padding: 6px 0; text-align: center; }
.from-section {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid var(--border, #e0e4f0);
}
.from-section-title {
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--text-secondary, #6c7a9c);
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.no-mappings-msg {
  font-size: 0.82rem;
  color: #92400e;
  background: #fffbeb;
  border: 1px solid #fcd34d;
  border-radius: 6px;
  padding: 8px 10px;
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}
.no-mappings-msg a { color: #b45309; font-weight: 600; }

.disabled-import {
  text-align: center;
  padding: 20px;
  background: #fafbff;
}
.import-hint { color: var(--text-secondary, #6c7a9c); font-size: 0.875rem; display: flex; align-items: center; justify-content: center; gap: 8px; }

/* Line Items */
.line-items-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; }
.btn-sm-add {
  background: #f0f3ff;
  color: var(--primary, #2c3e8c);
  border: 1px solid #c5cdf5;
  border-radius: 6px;
  padding: 5px 12px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.15s;
}
.btn-sm-add:hover { background: #e0e4f8; }

.line-items-list { display: flex; flex-direction: column; gap: 10px; margin-bottom: 16px; }

.line-item-row {
  border: 1px solid var(--border, #e0e4f0);
  border-radius: 8px;
  padding: 12px;
  background: #fafbff;
  border-left: 3px solid var(--primary, #2c3e8c);
}

.item-GITHUB_COMMIT { border-left-color: #7b1fa2; }
.item-TOKEN_USAGE { border-left-color: #1565c0; }
.item-SUBSCRIPTION { border-left-color: #2e7d32; }
.item-EXPENSE { border-left-color: #f57f17; }
.item-HOURLY { border-left-color: #00695c; }

.item-type-tag { margin-bottom: 8px; }

.type-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.badge-FLAT_FEE      { background: #e8eaf6; color: #3949ab; }
.badge-HOURLY        { background: #e0f2f1; color: #00695c; }
.badge-EXPENSE       { background: #fff8e1; color: #f57f17; }
.badge-GITHUB_COMMIT { background: #f3e5f5; color: #6a1b9a; }
.badge-TOKEN_USAGE   { background: #e3f2fd; color: #1565c0; }
.badge-SUBSCRIPTION  { background: #e8f5e9; color: #2e7d32; }

.item-description-row { display: flex; gap: 8px; align-items: flex-start; margin-bottom: 8px; }
.item-description {
  flex: 1;
  padding: 6px 10px;
  border: 1px solid var(--border, #e0e4f0);
  border-radius: 6px;
  font-size: 0.85rem;
  resize: vertical;
  background: #fff;
}

.remove-btn {
  background: none;
  border: 1px solid #fce4ec;
  border-radius: 6px;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #c62828;
  font-size: 0.85rem;
  flex-shrink: 0;
  transition: all 0.15s;
}
.remove-btn:hover { background: #fce4ec; }

.item-amounts-row {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 8px;
}
.amount-group { display: flex; flex-direction: column; gap: 3px; }
.amount-group label { font-size: 0.7rem; }
.amount-input {
  padding: 6px 8px;
  border: 1px solid var(--border, #e0e4f0);
  border-radius: 6px;
  font-size: 0.85rem;
  text-align: right;
  background: #fff;
}
.amount-total { background: #f8f9ff; font-weight: 600; }

.no-items {
  text-align: center;
  padding: 32px 16px;
  color: var(--text-secondary, #6c7a9c);
  font-size: 0.875rem;
}
.no-items i { font-size: 2rem; margin-bottom: 8px; opacity: 0.3; display: block; }

.totals-section {
  border-top: 2px solid var(--border, #e0e4f0);
  padding-top: 16px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
}

.totals-row {
  display: flex;
  justify-content: flex-end;
  gap: 24px;
  font-size: 0.9rem;
  color: var(--text-primary, #1a1a2e);
  min-width: 240px;
}

.grand-total {
  font-size: 1.1rem;
  font-weight: 700;
  padding-top: 8px;
  border-top: 1px solid var(--border, #e0e4f0);
  margin-top: 4px;
}
</style>
